import { cn } from '@/lib/utils';

import { BlockReference } from '@/cms/types/block';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { isBuildPhase } from '@/constant/env';
import Footer from '@/decodo/blocks/Footer';
import FooterCategories, {
  FooterCategoriesProps,
} from '@/decodo/blocks/Footer/FooterCategories';
import { Divider } from '@/decodo/components/atoms/Divider';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import { ServiceStatus } from '@/decodo/components/atoms/Instatus';
import { trackCacheHit } from '@/utils/fetch';

import FooterContacts, { FooterContactsProps } from './FooterContacts';
import { fetchFooterData } from './utils';

export interface FooterStrapiProps {
  footer: {
    logo: ImageStrapiProps;
    contacts?: FooterContactsProps['contacts'];
    categories?: FooterCategoriesProps['categories'];
  };
}
type FooterStrapiPropsBlock = {
  block: BlockReference<FooterStrapiProps>;
};

export const getImageLinkTrackerContext = (
  context: string,
  url: string | undefined,
  index: number
) => {
  let result = `${context}-image-link-`;

  if (!url) {
    result += index;
    return result;
  }

  if (url.startsWith('mailto:')) {
    result += url.replace('mailto:', '').split('@')[0];
    return result;
  }

  try {
    const domain = new URL(url).hostname;
    result += domain;

    return result;
  } catch (e) {
    console.error('Error parsing URL', e);
    return (result += 'unknown');
  }
};

export const transformFooterStrapiProps = ({ footer }: FooterStrapiProps) => {
  const hasFooterLinks =
    !!footer?.contacts?.length || !!footer?.categories?.length;

  return {
    footer,
    hasFooterLinks,
  };
};

export const mutualLinkStyle =
  'text-body-m font-mono text-gray-950 dark:text-base-dark-white hover:text-brand-500 dark:hover:text-brand-500';

const buildFooterCache = new Map<string, FooterStrapiProps>();

const getCachedFooterData = async (
  documentId: string
): Promise<FooterStrapiProps> => {
  // During build, check cache first
  if (isBuildPhase && buildFooterCache.has(documentId)) {
    trackCacheHit();
    return buildFooterCache.get(documentId)!;
  }

  const footerData = await fetchFooterData(documentId);

  const data = footerData?.data?.[0]?.attributes;

  if (!data) {
    throw new Error(`footer with ID ${documentId} not found`);
  }

  // Cache during build
  if (isBuildPhase) {
    buildFooterCache.set(documentId, data);
  }

  return data;
};

const discriminatorlyGetProps = async (
  block: FooterStrapiPropsBlock['block']
) => {
  if (!block.data) return null;

  let data = undefined as FooterStrapiProps | undefined;

  // treat logo as a switch case
  if (block && block.data.attributes && block.data.attributes.footer) {
    data = block.data.attributes as FooterStrapiProps;
  } else {
    if (!block.data.documentId) {
      throw new Error('Document ID is required');
    }

    data = await getCachedFooterData(block.data.documentId);
  }

  return data;
};

export default async function FooterStrapi({ block }: FooterStrapiPropsBlock) {
  const data: FooterStrapiProps | null = await discriminatorlyGetProps(block);

  if (!data) return null;

  const { footer, hasFooterLinks } = transformFooterStrapiProps(data);

  return (
    <Footer>
      <div className={cn(hasFooterLinks && 'mb-8 md:mb-12')}>
        <ImageStrapi
          inner={{
            classNames: {
              image: 'h-5',
            },
          }}
          block={footer.logo}
        />
      </div>
      <div
        className='flex flex-col flex-nowrap gap-10 md:flex-row md:gap-20 '
        data-tracker-section={SECTION_NAMES.FOOTER}
      >
        <div
          className={cn(
            'grid h-fit grid-rows-[auto_auto] gap-8 md:gap-[60px]',
            (footer.contacts?.length || 0) > 2
              ? 'grid-cols-1 md:grid-cols-[200px_200px]'
              : 'grid-cols-1 md:grid-cols-[200px]'
          )}
        >
          {!!footer.contacts?.length && (
            <FooterContacts contacts={footer.contacts} />
          )}
          <ServiceStatus
            size='sm'
            classNames={{
              // force a slightly wider button width to fit the
              // text that's there 99% of the time - "All systems operational"
              button: cn(
                'w-[200px] text-gray-950 dark:text-base-dark-white',
                footer.contacts && !footer.contacts.length && 'mt-6'
              ),
            }}
          />
        </div>
        {!!footer.categories?.length && (
          <div className='hidden md:block'>
            <Divider
              showOnDark
              alignment='vertical'
              classNames={{
                root: 'dark:border-gray-dark-700 border-gray-200',
              }}
            />
          </div>
        )}
        {!!footer.categories?.length && (
          <FooterCategories categories={footer?.categories} />
        )}
      </div>
    </Footer>
  );
}
